// === UID FACTORY ===
// Source de vérité unique pour la génération d'UIDs d'items/consommables.
// Remplace les compteurs indépendants de InventoryManager et CampManager,
// qui pouvaient collisionner quand un item voyageait entre les deux sources
// (loot run → coffre, craft → coffre, coffre → équipement, etc.).
//
// Le compteur est unique et global, persisté en localStorage. Les préfixes
// (item_, camp_, cons_...) servent uniquement à la lisibilité pour le debug :
// la valeur numérique est monotone et partagée entre toutes les sources.

const STORAGE_KEY = 'deadtide_uid_counter';

class _UidFactory {
  private counter: number;

  constructor() {
    this.counter = this.load();
  }

  /**
   * Génère le prochain UID unique avec un préfixe optionnel.
   * Le compteur avance systématiquement : aucun UID généré ne peut être
   * identique à un autre UID déjà généré par cette factory.
   */
  next(prefix: string = 'item'): string {
    const uid = `${prefix}_${this.counter++}`;
    this.save();
    return uid;
  }

  /**
   * Scanne un UID existant (sauvegarde legacy, item déserialisé) et fait
   * avancer le compteur au-delà de sa valeur numérique si nécessaire.
   * Garantit que les UIDs générés ensuite ne collisionneront pas avec
   * ceux déjà présents dans la sauvegarde.
   */
  observe(uid: string): void {
    const m = uid.match(/(\d+)$/);
    if (!m) return;
    const n = parseInt(m[1], 10);
    if (!isNaN(n) && n >= this.counter) {
      this.counter = n + 1;
      this.save();
    }
  }

  /** Itère un lot d'UIDs et bump le compteur au-delà du max. */
  observeAll(uids: Iterable<string>): void {
    for (const uid of uids) this.observe(uid);
  }

  /** Valeur actuelle du compteur (debug/tests uniquement). */
  getCounter(): number {
    return this.counter;
  }

  /**
   * Réinitialise le compteur (tests uniquement — ne pas appeler en prod).
   * Les UIDs déjà émis restent valides mais peuvent être ré-émis après reset.
   */
  reset(): void {
    this.counter = 1;
    this.save();
  }

  private load(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const n = parseInt(raw, 10);
        if (!isNaN(n) && n >= 1) return n;
      }
    } catch { /* localStorage indisponible */ }
    return 1;
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(this.counter));
    } catch { /* localStorage plein ou indisponible */ }
  }
}

/** Instance singleton partagée par tous les systèmes qui créent des items. */
export const UidFactory = new _UidFactory();
